import { fetchActiveGameForPairing } from "../api/games.js";

/**
 * @param {object} opts
 * @param {import('@supabase/supabase-js').SupabaseClient} opts.supabase
 * @param {string} opts.userId
 * @param {(detail: { pairingId: string, gameId: string | null, mode: 'resume' | 'new' }) => void} opts.onEnterGame
 */
export async function renderPairHub(opts) {
  const { supabase, userId, onEnterGame } = opts;
  const container = document.createElement("div");
  container.className = "pair-hub";
  container.innerHTML = `<h2>Your pairings</h2><p>Loading…</p>`;

  const { data: pairings, error } = await supabase
    .from("pairings")
    .select("id, player_a_id, player_b_id")
    .or(`player_a_id.eq.${userId},player_b_id.eq.${userId}`);

  if (error) {
    container.innerHTML = `<p class="pair-hub">Failed to load pairings: ${error.message}</p>`;
    return container;
  }

  if (!pairings?.length) {
    container.innerHTML = `<p>No pairings are configured for your account. Ask an admin to add a row in <code>pairings</code> linking you and your opponent.</p>`;
    return container;
  }

  const opponentIds = pairings.map((p) =>
    p.player_a_id === userId ? p.player_b_id : p.player_a_id
  );
  const { data: profiles } = await supabase
    .from("profiles")
    .select("id, display_name, avatar_url")
    .in("id", opponentIds);
  /** @type {Map<string, { display_name: string, avatar_url: string | null }>} */
  const profileById = new Map(
    (profiles ?? []).map((row) => [
      row.id,
      {
        display_name: row.display_name,
        avatar_url: row.avatar_url ?? null,
      },
    ])
  );

  /**
   * @param {string} name
   * @param {string | null} avatarUrl
   */
  function createOpponentAvatar(name, avatarUrl) {
    if (avatarUrl) {
      const img = document.createElement("img");
      img.className = "pair-card-avatar";
      img.src = avatarUrl;
      img.alt = "";
      img.referrerPolicy = "no-referrer";
      img.width = 48;
      img.height = 48;
      return img;
    }
    const fallback = document.createElement("div");
    fallback.className = "pair-card-avatar pair-card-avatar-fallback";
    fallback.setAttribute("aria-hidden", "true");
    fallback.textContent = (name.trim()[0] ?? "?").toUpperCase();
    return fallback;
  }

  const list = document.createElement("div");
  list.className = "pair-list";

  for (const p of pairings) {
    const opponentId = p.player_a_id === userId ? p.player_b_id : p.player_a_id;
    const profile = profileById.get(opponentId);
    const opponentName = profile?.display_name ?? "Opponent";
    const { game: active } = await fetchActiveGameForPairing(supabase, p.id);

    const card = document.createElement("div");
    card.className = "pair-card";

    const header = document.createElement("div");
    header.className = "pair-card-header";
    header.appendChild(createOpponentAvatar(opponentName, profile?.avatar_url ?? null));
    const heading = document.createElement("strong");
    heading.textContent = ` ${opponentName}`;
    header.appendChild(heading);
    card.appendChild(header);

    const actions = document.createElement("div");
    actions.className = "pair-card-actions";

    const newBtn = document.createElement("button");
    newBtn.type = "button";
    newBtn.textContent = "New game";
    newBtn.addEventListener("click", () => {
      onEnterGame({ pairingId: p.id, gameId: null, mode: "new" });
    });

    const resumeBtn = document.createElement("button");
    resumeBtn.type = "button";
    resumeBtn.textContent = "Resume";
    resumeBtn.disabled = !active?.id;
    resumeBtn.addEventListener("click", () => {
      if (active?.id) onEnterGame({ pairingId: p.id, gameId: active.id, mode: "resume" });
    });

    actions.appendChild(resumeBtn);
    actions.appendChild(newBtn);
    card.appendChild(actions);
    list.appendChild(card);
  }

  const title = document.createElement("h2");
  title.textContent = "Your pairings";
  container.replaceChildren();
  container.appendChild(title);
  container.appendChild(list);
  return container;
}
